import videojs from "video.js";

const TimeDisplay = videojs.getComponent("TimeDisplay");
const RemainingTimeDisplay = videojs.getComponent("RemainingTimeDisplay");
const TimeTooltip = videojs.getComponent("TimeTooltip");
const SeekBar = videojs.getComponent("SeekBar");
const Slider = videojs.getComponent("Slider");

const pad = (value, size = 2) => value.toString().padStart(size, "0");

function computedStyle(el, property) {
  const styles = getComputedStyle(el);
  return el[property] ?? null;
}

/* from video.js/src/js/utils/dom.js */
function getBoundingClientRect(el) {
  if (el && el.getBoundingClientRect && el.parentNode) {
    const rect = el.getBoundingClientRect();
    const result = {};

    ['bottom', 'height', 'left', 'right', 'top', 'width'].forEach(k => {
      if (rect[k] !== undefined) {
        result[k] = rect[k];
      }
    });

    if (!result.height) {
      result.height = parseFloat(computedStyle(el, 'height'));
    }

    if (!result.width) {
      result.width = parseFloat(computedStyle(el, 'width'));
    }

    return result;
  }
}

const formatWithMs = seconds => {
  if (!Number.isFinite(seconds)) {
    return "00:00.000";
  }

  const totalMillis = Math.floor(Math.abs(seconds) * 1000);
  const millis = pad(totalMillis % 1000, 3);
  const totalSeconds = Math.floor(Math.abs(seconds));
  const secs = pad(totalSeconds % 60);
  const mins = pad(Math.floor(totalSeconds / 60) % 60);
  const hours = Math.floor(totalSeconds / 3600);

  const base = hours > 0 ? `${pad(hours)}:${mins}:${secs}` : `${mins}:${secs}`;
  const sign = seconds < 0 ? "-" : "";

  return `${sign}${base}.${millis}`;
};

export function overrideBaseTimeDisplayWithMs() {
  if (!TimeDisplay) {
    return;
  }

  const originalUpdate = TimeDisplay.prototype.updateTextNode_;

  if (originalUpdate && originalUpdate.__withMilliseconds) {
    return;
  }

  TimeDisplay.prototype.updateTextNode_ = function(time = 0) {
    time = formatWithMs(time);

    if (this.formattedTime_ === time) {
      return;
    }

    this.formattedTime_ = time;

    this.requestAnimationFrame(() => {
      if (!this.contentEl_) {
        return;
      }

      const oldNode = this.textNode_;

      this.textNode_ = document.createTextNode(this.formattedTime_);

      if (!this.textNode_) {
        return;
      }

      if (oldNode) {
        this.contentEl_.replaceChild(this.textNode_, oldNode);
      } else {
        this.contentEl_.appendChild(this.textNode_);
      }
    });

  };

  RemainingTimeDisplay.prototype.updateContent = function(){
    if (typeof this.player_.duration() !== 'number') {
      return;
    }

    let time;

    if (this.player_.ended()) {
      time = 0;
    } else {
      time = this.player_.remainingTime();
    }

    this.updateTextNode_(time);
  }

  TimeTooltip.prototype.updateTime = function(seekBarRect, seekBarPoint, time, cb) {
    // If there is an existing rAF ID, cancel it so we don't over-queue.
    if (this.rafId_) {
      this.cancelAnimationFrame(this.rafId_);
    }

    this.rafId_ = this.requestAnimationFrame(() => {
      let content;
      const duration = this.player_.duration();

      if (this.player_.liveTracker && this.player_.liveTracker.isLive()) {
        const liveWindow = this.player_.liveTracker.liveWindow();
        const secondsBehind = liveWindow - (seekBarPoint * liveWindow);

        content = (secondsBehind < 1 ? '' : '-') + formatWithMs(secondsBehind, liveWindow);
      } else {
        content = formatWithMs(time, duration);
      }

      this.update(seekBarRect, seekBarPoint, content);
      if (cb) {
        cb();
      }
    });
  }

  SeekBar.prototype.update = function(event) {
    const percent = Slider.prototype.update.call(this);

    this.requestAnimationFrame(() => {
      const currentTime = this.player_.ended() ?
        this.player_.duration() : this.getCurrentTime_();
      const liveTracker = this.player_.liveTracker;
      let duration = this.player_.duration();

      if (liveTracker && liveTracker.isLive()) {
        duration = this.player_.liveTracker.liveCurrentTime();
      }

      if (this.percent_ !== percent) {
        // machine readable value of progress bar (percentage complete)
        this.el_.setAttribute('aria-valuenow', (percent * 100).toFixed(2));
        this.percent_ = percent;
      }

      if (this.currentTime_ !== currentTime || this.duration_ !== duration) {
        // human readable value of progress bar (time complete)
        this.el_.setAttribute(
          'aria-valuetext',
          this.localize(
            'progress bar timing: currentTime={1} duration={2}',
            [formatWithMs(currentTime, duration),
              formatWithMs(duration, duration)],
            '{1} of {2}'
          )
        );

        this.currentTime_ = currentTime;
        this.duration_ = duration;
      }

      // update the progress bar time tooltip with the current time
      if (this.bar) {
        this.bar.update(getBoundingClientRect(this.el()), this.getProgress());
      }
    });

    return percent;
  }
}
